import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowUpRight, Sparkles, Target, MessageSquare, Calendar,
  CheckCircle2, Quote, Search, Users2, Clock3, Compass,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Sector → display theme (keeps cards editorial, not generic)
const SECTOR_CHIPS = [
  { key: 'all', label: 'All Mentors' },
  { key: 'banking', label: 'Finance & Banking', match: ['Banking', 'Finance', 'Investment'] },
  { key: 'tech', label: 'Tech & AI', match: ['Technology', 'Tech', 'AI', 'Software'] },
  { key: 'entrepreneurship', label: 'Founders', match: ['Entrepreneurship', 'Beauty', 'Retail', 'Consumer', 'E-commerce', 'Fashion'] },
  { key: 'policy', label: 'Policy & Government', match: ['Government', 'Policy', 'Politics', 'Public'] },
  { key: 'media', label: 'Media & Creative', match: ['Media', 'Entertainment', 'Film', 'Creative', 'Music'] },
  { key: 'impact', label: 'Social Impact', match: ['Impact', 'Nonprofit', 'Education', 'Health'] },
];

const SESSION_AGENDA = [
  { time: '0:00', title: 'The Check-in', desc: 'Ten minutes of honest talk. What is really going on this quarter?' },
  { time: '0:10', title: 'The Real Question', desc: 'Bring one problem. Not ten. The one that keeps you awake at 3am.' },
  { time: '0:25', title: 'Her Story, Not a Framework', desc: 'She tells you what she did at your stage. Unpolished. Useful.' },
  { time: '0:45', title: 'Three Things to Try', desc: 'Before the next session, you leave with three concrete moves.' },
];

const WHY_THEY_MENTOR = {
  quote:
    'I mentor because someone once took a meeting with me when she did not have to. I remember exactly what she said. I am still here because of it.',
  attribution: 'A mentor in the network',
  role: 'Repeat founder · Asia Pacific',
};

const FAQS = [
  {
    q: 'Is this really free?',
    a: 'Yes. Our mentors volunteer their time because someone once did the same for them. The cost is your commitment to show up, do the work, and — later — pay it forward.',
  },
  {
    q: 'How long is the commitment?',
    a: 'Three sessions over three months. Roughly sixty minutes each, virtual or in-person. Some pairs continue organically; many do.',
  },
  {
    q: 'How does the matching work?',
    a: 'We match on industry, stage, region, and the specific question you bring. No algorithm can guarantee chemistry — so the first session is a free chemistry call. If it is not right, we rematch.',
  },
  {
    q: 'I am further along — can I mentor?',
    a: 'Yes. If you have built something real, we want you in the network. Apply below and note "Mentor" in your message.',
  },
];

const MentorshipPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChip, setActiveChip] = useState('all');
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', company: '', title: '', sector: '', linkedin: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/api/stories`);
        if (!cancelled) setStories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('mentor stories load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Pick a curated pool of mentors - prioritize profiles with photos + quotes + featured
  const mentors = useMemo(() => {
    const pool = stories.filter(s => s.image_url && s.name && s.title);
    pool.sort((a, b) => {
      const aScore = (a.featured ? 2 : 0) + (a.quotes?.length ? 1 : 0);
      const bScore = (b.featured ? 2 : 0) + (b.quotes?.length ? 1 : 0);
      return bScore - aScore;
    });
    return pool.slice(0, 18);
  }, [stories]);

  const filteredMentors = useMemo(() => {
    if (activeChip === 'all') return mentors;
    const chip = SECTOR_CHIPS.find(c => c.key === activeChip);
    if (!chip || !chip.match) return mentors;
    return mentors.filter(m =>
      chip.match.some(k => (m.sector || '').toLowerCase().includes(k.toLowerCase()) || (m.title || '').toLowerCase().includes(k.toLowerCase()))
    );
  }, [mentors, activeChip]);

  // Hero collage — first 5 portraits
  const heroCollage = mentors.slice(0, 5);

  const featuredSpotlight = mentors[0];

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill name, email, and tell us what you need.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/participate/mentor`, form);
      setSubmitted(true);
      toast.success('Application received. We will reach out within 5 business days.');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="mentorship-page">
      {/* ========== HERO ========== */}
      <section className="relative pt-36 pb-24 overflow-hidden">
        {/* soft background grain */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(#1A1A1A 1px, transparent 1px)', backgroundSize: '18px 18px' }} />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-[#C25E3E]/10 border border-[#C25E3E]/20 rounded-full">
              <Sparkles size={12} className="text-[#C25E3E]" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                Invitation-Only · Free for Members
              </span>
            </div>

            <h1
              className="text-5xl lg:text-[78px] leading-[0.95] tracking-tight mb-8 text-[#1A1A1A]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Sit across the table<br />
              from a woman<br />
              <span className="italic text-[#C25E3E]">who already built it.</span>
            </h1>

            <p className="text-lg lg:text-xl max-w-xl mb-10 leading-relaxed" style={{ fontFamily: "'Lora', serif", color: '#3A3A3A' }}>
              Not a webinar. Not a course. A matched, three-session mentorship with one of <strong>109 vetted women leaders</strong> across finance, tech, policy, and impact — each of them built something real before they decided to turn around and reach back.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button
                onClick={scrollToForm}
                data-testid="mentorship-hero-cta"
                className="group inline-flex items-center gap-2 px-8 h-14 bg-[#C25E3E] text-white text-[12px] tracking-[0.18em] uppercase hover:bg-[#1A1A1A] transition-all duration-300"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
              >
                Request a Mentor
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <Link
                to="/profiles"
                className="inline-flex items-center gap-2 px-8 h-14 border border-[#1A1A1A]/25 text-[#1A1A1A] text-[12px] tracking-[0.18em] uppercase hover:border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-all"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
              >
                Browse the Leaders
              </Link>
            </div>

            {/* stats strip */}
            <div className="grid grid-cols-4 gap-6 max-w-lg pt-8 border-t border-[#1A1A1A]/10">
              {[
                { n: '109', l: 'Mentors' },
                { n: '15+', l: 'Regions' },
                { n: '3', l: 'Sessions' },
                { n: '0', l: 'AED Cost' },
              ].map((s) => (
                <div key={s.l}>
                  <p className="text-3xl lg:text-4xl font-light text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.n}</p>
                  <p className="text-[9px] tracking-[0.25em] uppercase text-[#888]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>{s.l}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Hero collage — asymmetric overlapping portraits */}
          <div className="relative h-[520px] hidden lg:block">
            {heroCollage.length > 0 && (
              <>
                <div className="absolute top-0 right-0 w-[300px] h-[380px] overflow-hidden shadow-2xl transform rotate-2 border-4 border-white" data-testid="hero-portrait-0">
                  <img src={heroCollage[0].image_url} alt={heroCollage[0].name} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-white text-[15px]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{heroCollage[0].name}</p>
                    <p className="text-white/70 text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{heroCollage[0].title}</p>
                  </div>
                </div>
                {heroCollage[1] && (
                  <div className="absolute top-[180px] left-0 w-[230px] h-[300px] overflow-hidden shadow-2xl transform -rotate-3 border-4 border-white" data-testid="hero-portrait-1">
                    <img src={heroCollage[1].image_url} alt={heroCollage[1].name} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="text-white text-[13px]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{heroCollage[1].name}</p>
                      <p className="text-white/70 text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{heroCollage[1].title}</p>
                    </div>
                  </div>
                )}
                {heroCollage[2] && (
                  <div className="absolute bottom-0 right-[60px] w-[180px] h-[230px] overflow-hidden shadow-xl transform rotate-6 border-4 border-white" data-testid="hero-portrait-2">
                    <img src={heroCollage[2].image_url} alt={heroCollage[2].name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                {/* decorative signature plate */}
                <div className="absolute top-4 left-10 bg-white shadow-lg px-5 py-4 transform -rotate-6 z-10">
                  <p className="text-[9px] tracking-[0.25em] uppercase text-[#888]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Session Notes</p>
                  <p className="text-[13px] mt-1 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>"Ask her the question you're afraid to ask anyone else."</p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ========== MEET YOUR MENTORS ========== */}
      <section className="py-24 bg-white border-y border-[#1A1A1A]/8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 gap-6">
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase mb-3 text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>The Mentors</p>
              <h2 className="text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1A1A1A] max-w-2xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                These are the women who will take your call.
              </h2>
            </div>
            <Link to="/profiles" className="group inline-flex items-center gap-2 text-[12px] tracking-[0.15em] uppercase text-[#1A1A1A] hover:text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
              View all 370+ leaders <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="mentor-filter-chips">
            {SECTOR_CHIPS.map((c) => (
              <button
                key={c.key}
                onClick={() => setActiveChip(c.key)}
                data-testid={`chip-${c.key}`}
                className={`px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase border transition-all ${
                  activeChip === c.key
                    ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                    : 'bg-white text-[#4A4A4A] border-[#1A1A1A]/20 hover:border-[#1A1A1A]'
                }`}
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Mentor grid */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-[#F2EFE8] animate-pulse" />
              ))}
            </div>
          ) : filteredMentors.length === 0 ? (
            <div className="py-16 text-center text-[#888] italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>No mentors match this filter yet — try another category.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="mentor-grid">
              {filteredMentors.slice(0, 12).map((m) => (
                <Link
                  to={`/profiles/${m.slug}`}
                  key={m.slug}
                  className="group relative aspect-[3/4] overflow-hidden bg-[#1A1A1A] block"
                  data-testid={`mentor-card-${m.slug}`}
                >
                  <img src={m.image_url} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white text-[14px] leading-tight mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{m.name}</p>
                    <p className="text-white/70 text-[9px] tracking-[0.15em] uppercase leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.title}</p>
                  </div>
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-8 h-8 bg-[#C25E3E] flex items-center justify-center">
                      <ArrowUpRight size={14} className="text-white" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ========== SAMPLE SESSION AGENDA ========== */}
      <section className="py-24 bg-[#1A1A1A] text-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-5 gap-16">
          <div className="lg:col-span-2">
            <p className="text-[11px] tracking-[0.3em] uppercase mb-3 text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Inside the Room</p>
            <h2 className="text-4xl lg:text-5xl leading-[1.05] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              What happens in <span className="italic text-[#C25E3E]">sixty minutes</span>.
            </h2>
            <p className="text-base leading-relaxed text-white/70 mb-8" style={{ fontFamily: "'Lora', serif" }}>
              No pitch decks. No networking small talk. One real problem, one honest operator, and three concrete moves before you log off.
            </p>
            <div className="flex items-center gap-3 text-[11px] tracking-[0.2em] uppercase text-white/60" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
              <Clock3 size={14} /> 60 min · virtual or in-person
            </div>
          </div>
          <div className="lg:col-span-3 relative">
            <div className="absolute left-[26px] top-2 bottom-2 w-px bg-white/15" aria-hidden />
            <div className="space-y-8">
              {SESSION_AGENDA.map((s, i) => (
                <div key={s.time} className="relative pl-16" data-testid={`session-step-${i}`}>
                  <div className="absolute left-0 top-0 w-[54px] h-[54px] rounded-full bg-[#C25E3E] flex items-center justify-center text-white font-light text-[13px] tracking-wider" style={{ fontFamily: "'DM Mono', monospace" }}>
                    {s.time}
                  </div>
                  <h3 className="text-2xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{s.title}</h3>
                  <p className="text-[14px] text-white/70 leading-relaxed" style={{ fontFamily: "'Lora', serif" }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== WHY THEY MENTOR - EDITORIAL QUOTE ========== */}
      <section className="py-28 bg-[#FAF8F4]">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12 text-center">
          <Quote size={36} className="mx-auto mb-8 text-[#C25E3E]" />
          <p className="text-3xl lg:text-[44px] leading-[1.15] mb-8 text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontStyle: 'italic' }}>
            "{WHY_THEY_MENTOR.quote}"
          </p>
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>— {WHY_THEY_MENTOR.attribution}</p>
          <p className="text-[12px] text-[#888] mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{WHY_THEY_MENTOR.role}</p>
        </div>
      </section>

      {/* ========== HOW MATCHING WORKS — 4 CARDS ========== */}
      <section className="py-24 bg-white border-y border-[#1A1A1A]/8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-[11px] tracking-[0.3em] uppercase mb-3 text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>How It Works</p>
            <h2 className="text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              From request to real advice in <span className="italic text-[#C25E3E]">four steps</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', icon: Search, title: 'You tell us your goal', desc: 'Two minutes. Your stage, your industry, the one question keeping you up. We read every submission.' },
              { step: '02', icon: Compass, title: 'We match human, not algo', desc: 'Matches are hand-curated by our team. Industry, region, stage, and — most important — chemistry.' },
              { step: '03', icon: MessageSquare, title: 'Chemistry call', desc: 'Thirty minutes to see if it clicks. No-fault rematch if not. We want the right pair, not a pair.' },
              { step: '04', icon: Calendar, title: 'Three sessions, real work', desc: 'Monthly for 60 minutes. Between sessions, you try, you stumble, you report back. That is the work.' },
            ].map((s) => (
              <div key={s.step} className="group p-8 bg-[#FAF8F4] border border-[#1A1A1A]/8 hover:border-[#C25E3E] hover:shadow-lg transition-all duration-300" data-testid={`match-step-${s.step}`}>
                <span className="text-[11px] tracking-[0.25em] text-[#C25E3E] block mb-4" style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{s.step}</span>
                <s.icon size={28} className="mb-5 text-[#1A1A1A] group-hover:text-[#C25E3E] transition-colors" />
                <h3 className="text-2xl mb-3 text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{s.title}</h3>
                <p className="text-[14px] leading-relaxed text-[#4A4A4A]" style={{ fontFamily: "'Lora', serif" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FEATURED MENTOR SPOTLIGHT ========== */}
      {featuredSpotlight && (
        <section className="py-24 bg-[#FAF8F4]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <p className="text-[11px] tracking-[0.3em] uppercase mb-10 text-[#C25E3E] text-center" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
              Featured Mentor
            </p>
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden bg-[#1A1A1A] shadow-2xl" data-testid="featured-mentor-image">
                  <img src={featuredSpotlight.image_url} alt={featuredSpotlight.name} className="w-full h-full object-cover" />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-[#C25E3E] text-white px-6 py-4 shadow-xl hidden sm:block">
                  <p className="text-[9px] tracking-[0.25em] uppercase opacity-80" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Open to mentoring</p>
                  <p className="text-xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{featuredSpotlight.region || 'Global'}</p>
                </div>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.25em] uppercase mb-3 text-[#888]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                  {featuredSpotlight.title} · {featuredSpotlight.company}
                </p>
                <h3 className="text-4xl lg:text-5xl leading-[1.05] mb-6 text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                  {featuredSpotlight.name}
                </h3>
                {featuredSpotlight.hook && (
                  <p className="text-lg italic text-[#C25E3E] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {featuredSpotlight.hook}
                  </p>
                )}
                <p className="text-base leading-relaxed text-[#3A3A3A] mb-8" style={{ fontFamily: "'Lora', serif" }}>
                  {(featuredSpotlight.story || '').split('\n')[0].slice(0, 320)}
                  {featuredSpotlight.story && featuredSpotlight.story.length > 320 ? '…' : ''}
                </p>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={scrollToForm}
                    data-testid="request-featured-mentor"
                    className="inline-flex items-center gap-2 px-7 h-12 bg-[#1A1A1A] text-white text-[11px] tracking-[0.18em] uppercase hover:bg-[#C25E3E] transition-all"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
                  >
                    Request This Mentor <ArrowRight size={14} />
                  </button>
                  <Link
                    to={`/profiles/${featuredSpotlight.slug}`}
                    className="inline-flex items-center gap-2 px-7 h-12 border border-[#1A1A1A]/25 text-[#1A1A1A] text-[11px] tracking-[0.18em] uppercase hover:border-[#1A1A1A] transition-all"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
                  >
                    Read Her Story
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ========== FAQ ========== */}
      <section className="py-24 bg-white border-y border-[#1A1A1A]/8">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <p className="text-[11px] tracking-[0.3em] uppercase mb-3 text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>FAQ</p>
          <h2 className="text-4xl lg:text-5xl leading-[1.05] tracking-tight mb-10 text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Honest answers.
          </h2>
          <div className="divide-y divide-[#1A1A1A]/10 border-y border-[#1A1A1A]/10">
            {FAQS.map((f, i) => (
              <button
                key={f.q}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full text-left py-6 group"
                data-testid={`faq-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-xl lg:text-2xl text-[#1A1A1A] group-hover:text-[#C25E3E] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                    {f.q}
                  </h3>
                  <span className="text-[#C25E3E] text-2xl font-light flex-shrink-0 mt-1 transition-transform duration-300" style={{ transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0)' }}>+</span>
                </div>
                {openFaq === i && (
                  <p className="mt-4 text-[15px] leading-relaxed text-[#4A4A4A]" style={{ fontFamily: "'Lora', serif" }}>{f.a}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ========== APPLICATION FORM ========== */}
      <section ref={formRef} className="py-28 bg-[#1A1A1A] text-white" id="apply">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          {submitted ? (
            <div className="text-center py-10" data-testid="mentorship-success">
              <CheckCircle2 size={64} className="mx-auto mb-6 text-[#C25E3E]" />
              <h3 className="text-4xl lg:text-5xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                We have your request.
              </h3>
              <p className="text-lg text-white/70 max-w-lg mx-auto" style={{ fontFamily: "'Lora', serif" }}>
                Every application is read by a human on our team. You will hear from us within 5 business days with a handpicked match — or a note if we think you are better served waiting for the right fit.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-12">
                <p className="text-[11px] tracking-[0.3em] uppercase mb-3 text-[#C25E3E]" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>The Application</p>
                <h2 className="text-4xl lg:text-6xl leading-[1.05] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                  Take the meeting.
                </h2>
                <p className="text-lg text-white/70 max-w-xl mx-auto" style={{ fontFamily: "'Lora', serif" }}>
                  Five fields. Two minutes. A human reads every one.
                </p>
              </div>
              <form onSubmit={submitApplication} className="space-y-6 max-w-2xl mx-auto" data-testid="mentorship-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase mb-2 text-white/60" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Full Name *</label>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full h-13 px-4 py-3 bg-transparent border border-white/20 text-white text-[15px] outline-none focus:border-[#C25E3E] transition-colors"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                      data-testid="mentor-form-name"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase mb-2 text-white/60" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Email *</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full h-13 px-4 py-3 bg-transparent border border-white/20 text-white text-[15px] outline-none focus:border-[#C25E3E] transition-colors"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                      data-testid="mentor-form-email"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase mb-2 text-white/60" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Company / Venture</label>
                    <input
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="w-full h-13 px-4 py-3 bg-transparent border border-white/20 text-white text-[15px] outline-none focus:border-[#C25E3E] transition-colors"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                      data-testid="mentor-form-company"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.2em] uppercase mb-2 text-white/60" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Your Role / Title</label>
                    <input
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full h-13 px-4 py-3 bg-transparent border border-white/20 text-white text-[15px] outline-none focus:border-[#C25E3E] transition-colors"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                      data-testid="mentor-form-title"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase mb-2 text-white/60" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Industry Focus</label>
                  <select
                    value={form.sector}
                    onChange={(e) => setForm({ ...form, sector: e.target.value })}
                    className="w-full h-13 px-4 py-3 bg-transparent border border-white/20 text-white text-[15px] outline-none focus:border-[#C25E3E] transition-colors"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid="mentor-form-sector"
                  >
                    <option value="" className="bg-[#1A1A1A]">Select an industry</option>
                    {SECTOR_CHIPS.filter(c => c.key !== 'all').map(c => (
                      <option key={c.key} value={c.label} className="bg-[#1A1A1A]">{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase mb-2 text-white/60" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>LinkedIn URL (optional)</label>
                  <input
                    value={form.linkedin}
                    onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/in/…"
                    className="w-full h-13 px-4 py-3 bg-transparent border border-white/20 text-white text-[15px] outline-none focus:border-[#C25E3E] transition-colors"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                    data-testid="mentor-form-linkedin"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.2em] uppercase mb-2 text-white/60" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                    What is the one question keeping you up at night? *
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Be specific. The more honest you are here, the better we can match you."
                    className="w-full px-4 py-3 bg-transparent border border-white/20 text-white text-[15px] outline-none focus:border-[#C25E3E] transition-colors resize-none"
                    style={{ fontFamily: "'Lora', serif" }}
                    data-testid="mentor-form-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-14 bg-[#C25E3E] text-white text-[12px] tracking-[0.2em] uppercase hover:bg-white hover:text-[#1A1A1A] transition-all disabled:opacity-50 disabled:cursor-wait"
                  style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
                  data-testid="mentorship-submit"
                >
                  {submitting ? 'Sending…' : 'Send My Application'}
                </button>
                <p className="text-[11px] text-white/50 text-center pt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Completely free · No sales pitch · A human replies within 5 business days
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      {/* ========== BECOME A MENTOR ========== */}
      <section className="py-20 bg-[#FAF8F4]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 text-center">
          <Users2 size={32} className="mx-auto mb-5 text-[#C25E3E]" />
          <h3 className="text-3xl lg:text-4xl mb-4 text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Already built something real? <span className="italic text-[#C25E3E]">Turn around.</span>
          </h3>
          <p className="text-base text-[#4A4A4A] max-w-lg mx-auto mb-8 leading-relaxed" style={{ fontFamily: "'Lora', serif" }}>
            We are always looking for more women willing to take the meeting with the one below her on the ladder. Three hours a quarter. Real impact. No platform fee.
          </p>
          <Link
            to="/participate"
            className="inline-flex items-center gap-2 px-8 h-13 py-4 bg-[#1A1A1A] text-white text-[12px] tracking-[0.2em] uppercase hover:bg-[#C25E3E] transition-all"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
            data-testid="become-mentor-cta"
          >
            Become a Mentor <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MentorshipPage;
